import {
  ActionLabel,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getPlaceholderForCustomReportType,
  getStatus,
  getStatusForConversionReportType,
  getStatusForCustomReportType,
  isFromScratch,
  isMinimalFiling,
  statusKeys
} from '../../../data';
import OutlinedNavigateIcon from '../../../../../../assets/icons/navigate-outlined.svg?react';
import { useAutomateTransformation } from '../../../../../../services/automation.service';
import { PermissionGuard } from '../../../../../../components/PermissionGuard';
import toast from 'react-hot-toast';
import { FaArrowRight } from 'react-icons/fa6';
import { useEffect, useMemo, useState } from 'react';
import { REPORT_TYPES } from '../../../../../../consts/reportTypes';
import { extractApiError } from '../../../../../../utils';

export const TransformationColumn = ({
  period,
  openDrawer,
  refetch,
  reportType = REPORT_TYPES.ANNEX_IV,
  isLoadingPeriods,
  canManageWorkflow = true
}) => {
  const status = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION) return getStatusForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) return getStatusForCustomReportType(period);

    return getStatus(period);
  }, [reportType, period]);
  const isScratch = isFromScratch(period);
  const placeholder = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION)
      return getPlaceholderForConversionReportType(period, reportType);
    else if (reportType === REPORT_TYPES.CUSTOM)
      return getPlaceholderForCustomReportType(period, reportType);

    return getPlaceholder(period, reportType);
  }, [reportType, period]);
  const automateTransformation = useAutomateTransformation();

  const [allowToShowLoadingPeriods, setAllowToShowLoadingPeriods] = useState(false);

  useEffect(() => {
    if (!isLoadingPeriods) setAllowToShowLoadingPeriods(false);
  }, [isLoadingPeriods]);

  const onAutoTransform = () => {
    automateTransformation.mutate(
      {
        period_id: period?.id
      },
      {
        onSuccess: () => {
          refetch();
          setAllowToShowLoadingPeriods(true);
          toast.success('Automated Transformation has been successfully applied', {
            position: 'bottom-center'
          });
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to start automatic transformation.'));
        }
      }
    );
  };

  const onClick = () => {
    openDrawer();
  };

  const onOpenIssues = () => {
    openDrawer();
  };

  const hasWarningsOrErrors =
    period?.transformation_stats?.in_progress_transformation_number > 0 ||
    period?.transformation_stats?.failed_transformations_number > 0 ||
    period?.transformation_stats?.not_completed_transformations_number > 0;

  const isNextStepsEnabled =
    status === statusKeys.TRANSFORMATION ||
    status === statusKeys.REPORT ||
    status === statusKeys.APPROVAL ||
    status === statusKeys.SUBMISSION ||
    status === statusKeys.END;
  if (automateTransformation.isLoading)
    return <ActionLabel style={{ width: 150 }} placeholder="Transforming..." />;

  if (isLoadingPeriods && allowToShowLoadingPeriods)
    return <ActionLabel style={{ width: 150 }} placeholder="Transforming..." />;

  if (isMinimalFiling(period)) return <ActionLabel style={{ width: 150 }} placeholder="N/A" />;

  if (
    status === statusKeys.TRANSFORMATION &&
    (!hasWarningsOrErrors || period?.transformation_progress_status === 'Not started')
  ) {
    return (
      <PermissionGuard allowed={canManageWorkflow}>
        <ActionLabel
          label="Start Transformation"
          rightIcon={<OutlinedNavigateIcon />}
          onClick={isScratch ? onClick : onAutoTransform}
        />
      </PermissionGuard>
    );
  }

  if (!hasWarningsOrErrors && period?.transformation_progress_status !== 'Not started')
    return (
      <ActionLabel
        style={{ width: 150 }}
        label="Finished"
        color="#40C057"
        onClick={onClick}
        rightIcon={<FaArrowRight />}
      />
    );

  if (hasWarningsOrErrors && isNextStepsEnabled)
    return (
      <>
        {period?.transformation_stats?.successful_transformations_number > 0 && (
          <ActionLabel
            label={period?.transformation_stats?.successful_transformations_number + ' successful'}
            color="#40C057"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.transformation_stats?.failed_transformations_number > 0 && (
          <ActionLabel
            label={(period?.transformation_stats?.failed_transformations_number || 0) + ' issues'}
            color="#FA5252"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.transformation_stats?.in_progress_transformation_number > 0 && (
          <ActionLabel
            label={period?.transformation_stats?.in_progress_transformation_number + ' in progress'}
            color="#228BE6"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.transformation_stats?.not_completed_transformations_number > 0 && (
          <ActionLabel
            label={
              (period?.transformation_stats?.not_completed_transformations_number || 0) +
              ' not transformed'
            }
            color="#7b7b7b"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}
      </>
    );

  if (
    period.transformation_progress_status === 'In progress' &&
    !period?.transformation_stats?.successful_transformations_number &&
    !isScratch &&
    isNextStepsEnabled
  )
    return (
      <ActionLabel
        label={0 + ' transformed'}
        color="#7b7b7b"
        onClick={onOpenIssues}
        rightIcon={<FaArrowRight />}
      />
    );

  if (period.transformation_progress_status === 'Not started')
    return <ActionLabel placeholder={placeholder} />;

  if (
    period.transformation_progress_status === 'Completed' ||
    period?.transformation_stats?.successful_transformations_number > 0 ||
    isScratch
  )
    return (
      <ActionLabel
        style={{ width: 150 }}
        label="Finished"
        color="#40C057"
        onClick={onClick}
        rightIcon={<FaArrowRight />}
      />
    );

  return <ActionLabel placeholder={placeholder} />;
};
